import { useNavigate } from "react-router-dom";
import { User, Building2, Landmark, Check, Minus, AlertTriangle, ArrowRight, Lightbulb } from "lucide-react";

const OPTIONS = [
  {
    key: "sole",
    icon: User,
    name: "Sole Proprietorship",
    tagline: "Just start selling — you and the business are the same thing.",
    cost: "$0 to start",
    costNote: "Optional $25 DBA filing with your county",
    rows: [
      { label: "Personal liability protection", value: false, note: "Your house, car and savings are on the line" },
      { label: "Texas state filing", value: "None", note: "No Certificate of Formation" },
      { label: "Ongoing paperwork", value: "Minimal", note: "No franchise tax report" },
      { label: "Taxes", value: "Schedule C", note: "All profit hit with 15.3% self-employment tax" },
      { label: "Looks credible to banks & clients", value: false },
    ],
    best: "Testing an idea with little risk and no employees",
  },
  {
    key: "llc",
    icon: Building2,
    name: "Texas LLC",
    highlight: true,
    tagline: "A separate legal entity that shields your personal assets.",
    cost: "$300 one-time",
    costNote: "State filing fee · no annual report fee in Texas",
    rows: [
      { label: "Personal liability protection", value: true, note: "Business debts stay with the business" },
      { label: "Texas state filing", value: "Form 205", note: "Certificate of Formation via SOSDirect" },
      { label: "Ongoing paperwork", value: "Light", note: "Annual franchise tax report (usually $0 owed)" },
      { label: "Taxes", value: "Pass-through", note: "Same as sole prop by default — no double tax" },
      { label: "Looks credible to banks & clients", value: true },
    ],
    best: "Almost every freelancer, contractor, online or local business in Texas",
  },
  {
    key: "scorp",
    icon: Landmark,
    name: "LLC + S-Corp election",
    tagline: "Same LLC, different tax treatment — file IRS Form 2553 later.",
    cost: "$300 + payroll",
    costNote: "Election is free; expect $500–$1,500/yr for payroll & tax prep",
    rows: [
      { label: "Personal liability protection", value: true, note: "Identical to a regular LLC" },
      { label: "Texas state filing", value: "Same LLC", note: "Nothing extra with the state" },
      { label: "Ongoing paperwork", value: "Heavier", note: "Payroll, W-2 for yourself, Form 1120-S" },
      { label: "Taxes", value: "Salary + distributions", note: "Can save on self-employment tax above ~$60k profit" },
      { label: "Looks credible to banks & clients", value: true },
    ],
    best: "Established LLCs netting $60k+ per year — you can elect it any time",
  },
];

function Value({ value }) {
  if (value === true) return <Check className="h-4 w-4 flex-shrink-0 text-emerald-600" aria-label="Yes" />;
  if (value === false) return <Minus className="h-4 w-4 flex-shrink-0 text-[#C8102E]" aria-label="No" />;
  return <span className="text-xs font-bold text-[#1E3A8A]">{value}</span>;
}

export default function EntityExplainer() {
  const navigate = useNavigate();
  return (
    <section id="is-an-llc-right" data-testid="entity-explainer" className="border-t-2 border-[var(--c-border)] px-4 py-16 sm:py-20">
      <div className="mx-auto max-w-6xl">
        <div className="max-w-2xl">
          <p className="mb-3 inline-flex rounded-full border-2 border-[var(--c-border)] bg-[var(--c-surface)] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-[#1E3A8A] shadow-[2px_2px_0px_var(--c-shadow)]">Before you file</p>
          <h2 className="font-heading text-3xl font-black leading-tight text-[var(--c-text)] sm:text-4xl">Is an LLC right for me?</h2>
          <p className="mt-3 text-base text-[var(--c-muted)]">
            Texas has no personal income tax, so the choice comes down to <strong className="text-[var(--c-text)]">liability protection</strong> and <strong className="text-[var(--c-text)]">how much paperwork you want</strong>. Here&apos;s the honest comparison.
          </p>
        </div>

        <div className="mt-10 grid gap-5 lg:grid-cols-3">
          {OPTIONS.map(({ key, icon: Icon, name, tagline, cost, costNote, rows, best, highlight }) => (
            <div
              key={key}
              data-testid={`entity-${key}`}
              className={`relative flex flex-col rounded-3xl border-2 border-[var(--c-border)] p-6 shadow-[5px_5px_0px_var(--c-shadow)] ${highlight ? "bg-[#1E3A8A] text-white" : "bg-[var(--c-surface)] text-[var(--c-text)]"}`}
            >
              {highlight && (
                <span className="absolute -top-3 left-6 rounded-full border-2 border-[var(--c-border)] bg-[#C8102E] px-3 py-0.5 text-[10px] font-black uppercase tracking-wide text-white">What this guide covers</span>
              )}
              <div className="flex items-center gap-3">
                <div className={`flex h-11 w-11 items-center justify-center rounded-xl border-2 ${highlight ? "border-white/40 bg-white/10" : "border-[var(--c-border)] bg-[#1E3A8A] text-white"}`}><Icon className="h-5 w-5" /></div>
                <div>
                  <h3 className="font-heading text-lg font-black leading-tight">{name}</h3>
                  <p className={`text-xs ${highlight ? "text-blue-100" : "text-[var(--c-muted)]"}`}>{tagline}</p>
                </div>
              </div>
              <div className={`mt-5 rounded-xl border-2 px-4 py-3 ${highlight ? "border-white/30 bg-white/10" : "border-[var(--c-border)] bg-[var(--c-bg)]"}`}>
                <p className="font-heading text-xl font-black">{cost}</p>
                <p className={`text-[11px] ${highlight ? "text-blue-100" : "text-[var(--c-muted)]"}`}>{costNote}</p>
              </div>
              <ul className="mt-5 flex-1 space-y-3">
                {rows.map((r) => (
                  <li key={r.label} className="text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-semibold">{r.label}</span>
                      <span className={highlight ? "[&_span]:text-[#A3E6D5] [&_svg]:text-[#A3E6D5]" : ""}><Value value={r.value} /></span>
                    </div>
                    {r.note && <p className={`text-[11px] leading-snug ${highlight ? "text-blue-100" : "text-[var(--c-muted)]"}`}>{r.note}</p>}
                  </li>
                ))}
              </ul>
              <p className={`mt-5 border-t-2 pt-4 text-xs ${highlight ? "border-white/30 text-blue-50" : "border-[var(--c-border)] text-[var(--c-muted)]"}`}>
                <span className="font-bold uppercase tracking-wide">Best for:</span> {best}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-col gap-4 rounded-2xl border-2 border-dashed border-[var(--c-border)] bg-[var(--c-surface)] p-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <Lightbulb className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#1E3A8A]" />
            <p className="text-sm text-[var(--c-text)]">
              <strong>Rule of thumb:</strong> if you&apos;ll sign contracts, take payments or have any chance of being sued, form the LLC. You can add the S-Corp election later once profit justifies payroll — the guide shows you where that decision point is.
            </p>
          </div>
          <button
            data-testid="entity-explainer-cta"
            onClick={() => navigate("/login?mode=register")}
            className="inline-flex flex-shrink-0 items-center gap-2 rounded-full border-2 border-[var(--c-border)] bg-[#1E3A8A] px-5 py-2.5 text-sm font-bold text-white shadow-[3px_3px_0px_var(--c-shadow)] transition-transform hover:-translate-y-0.5"
          >
            Start the LLC steps free <ArrowRight className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-3 flex items-center gap-1.5 text-[11px] text-[var(--c-muted)]"><AlertTriangle className="h-3 w-3" /> General information, not legal or tax advice. Confirm your situation with a Texas CPA or attorney.</p>
      </div>
    </section>
  );
}
